import React, { useEffect, useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiPlus, FiTrash, FiDownload, FiUpload, FiRefreshCw, FiAlertCircle, FiList } = FiIcons;

const ListsManager = () => {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [newItem, setNewItem] = useState('');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchLists(); }, []);

  const fetchLists = async () => {
    const { data } = await supabase.from('app_lists').select('*').order('label');
    if (data) {
      setLists(data);
      if (selectedList) {
        const updated = data.find(l => l.key === selectedList.key);
        if (updated) setSelectedList(updated);
      }
    }
  };

  const updateListInDB = async (items) => {
    setLoading(true);
    try {
      await supabase.from('app_lists').update({ items: items }).eq('key', selectedList.key);
      await fetchLists();
    } catch (err) { alert(err.message); } finally { setLoading(false); }
  };

  const handleAddItem = () => {
    if (!newItem || !selectedList) return;
    const updatedItems = [...(selectedList.items || []), newItem];
    updateListInDB(updatedItems);
    setNewItem('');
  };

  const handleRemoveItem = (item) => {
    const updatedItems = (selectedList.items || []).filter(i => i !== item);
    updateListInDB(updatedItems);
  };

  return (
    <div className="h-[calc(100vh-100px)] flex flex-col md:flex-row gap-6">
      <div className="w-full md:w-1/3 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden flex flex-col transition-colors">
        <div className="p-4 bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700 font-black text-slate-700 dark:text-slate-300 uppercase text-[10px] tracking-widest">
          Verfügbare Listen
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-slate-50 dark:divide-slate-800">
          {lists.map(lst => (
            <button 
              key={lst.key} 
              onClick={() => setSelectedList(lst)} 
              className={`w-full text-left px-6 py-4 transition-all ${selectedList?.key === lst.key ? 'bg-yellow-50 dark:bg-yellow-500/10 text-yellow-900 dark:text-yellow-500 border-l-4 border-yellow-500 font-bold' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
            >
              <div className="flex justify-between items-center">
                <span className="text-sm">{lst.label}</span>
                <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 bg-white dark:bg-slate-800 px-2 py-0.5 rounded-full border border-slate-100 dark:border-slate-700">
                  {lst.items?.length || 0}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col overflow-hidden transition-colors">
        {selectedList ? (
          <>
            <div className="p-4 border-b dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/20">
              <div>
                <p className="font-black text-slate-400 dark:text-slate-500 uppercase text-[10px] tracking-widest">Bearbeite Liste</p>
                <p className="text-xl font-black text-slate-900 dark:text-white">{selectedList.label}</p>
              </div>
              <div className="flex gap-2">
                <button className="p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 shadow-sm transition-colors">
                  <SafeIcon icon={FiDownload} />
                </button>
              </div>
            </div>
            
            <div className="p-4 border-b dark:border-slate-800 bg-white dark:bg-slate-900 flex gap-2">
              <input 
                type="text" 
                value={newItem} 
                onChange={(e) => setNewItem(e.target.value)} 
                placeholder="Neuer Eintrag..." 
                className="flex-1 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none"
                onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
              />
              <button 
                onClick={handleAddItem} 
                disabled={loading}
                className="bg-slate-900 dark:bg-yellow-500 text-white dark:text-slate-950 px-6 py-2 rounded-xl hover:bg-slate-800 dark:hover:bg-yellow-600 flex items-center shadow-lg transition-all active:scale-95 disabled:opacity-50"
              >
                {loading ? <SafeIcon icon={FiRefreshCw} className="animate-spin" /> : <SafeIcon icon={FiPlus} />}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 bg-slate-50/30 dark:bg-slate-950/20">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {selectedList.items?.map((item, idx) => (
                  <li key={idx} className="flex justify-between items-center p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 hover:border-yellow-200 dark:hover:border-yellow-500 transition-all group shadow-sm">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{item}</span>
                    <button onClick={() => handleRemoveItem(item)} className="text-slate-300 dark:text-slate-600 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-all">
                      <SafeIcon icon={FiTrash} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-300 dark:text-slate-800 p-10 text-center">
            <SafeIcon icon={FiList} className="text-6xl mb-4 opacity-50" />
            <h3 className="text-lg font-black uppercase tracking-tighter">Bereit für die Pflege</h3>
            <p className="text-sm max-w-xs mt-2 text-slate-400 dark:text-slate-600">Wähle eine Liste aus der linken Spalte aus.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListsManager;